import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

interface IModal {
  isOpen: boolean;
  onClose: () => void;
  /** Заголовок модалки. Если задан — рисуется шапка с заголовком и крестиком. */
  title?: string;
  /** Подзаголовок мелким серым шрифтом под заголовком. */
  description?: string;
  /** Размер модалки. По умолчанию `md` (480px). */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Дополнительные классы на корневом контейнере. */
  className?: string;
  children: React.ReactNode;
}

const SIZE_CLASS: Record<NonNullable<IModal['size']>, string> = {
  sm: 'max-w-[420px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[760px]',
  xl: 'max-w-[960px]',
};

export function MyModal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  children,
  className = '',
}: IModal) {
  const modalWrapperRef = useRef<HTMLDivElement>(null);

  // Закрытие по Esc — стандартная для модалок ожидание.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Закрытие по клику на оверлей; клики внутри модалки не закрывают.
  const onOverlayMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  const modalRoot =
    typeof document !== 'undefined'
      ? document.getElementById('modal-root')
      : null;
  if (!modalRoot) return null;

  return ReactDOM.createPortal(
    isOpen ? (
      <div
        className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40"
        onMouseDown={onOverlayMouseDown}
      >
        <div
          ref={modalWrapperRef}
          className={`relative w-full ${SIZE_CLASS[size]} max-h-[90vh] flex flex-col bg-white rounded-xl shadow-xl overflow-hidden ${className}`}
        >
          {(title || description) && (
            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-100 shrink-0">
              <div className="min-w-0">
                {title ? (
                  <h2 className="text-lg font-semibold text-[#2d2c2c]">
                    {title}
                  </h2>
                ) : null}
                {description ? (
                  <p className="text-sm text-gray-500 mt-0.5">{description}</p>
                ) : null}
              </div>
              <button
                type="button"
                aria-label="Закрыть"
                onClick={onClose}
                className="shrink-0 -mr-2 -mt-1 w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 6L18 18M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          )}
          <div className="px-6 py-6 overflow-y-auto flex-1">{children}</div>
        </div>
      </div>
    ) : null,
    modalRoot
  );
}
